import { useMemo } from "react";
import { useTermsAndConditions } from "@/queries/termsAndConditions/useTermsAndConditions";
import { usePowerGenerationInfo } from "@/queries/user/usePowerGenerationInfo";
import { usePersonalAllocations } from "@/queries/allocations/usePersonalAllocations";
import { useUser } from "@/queries/user/useUser";

export function useOutstandingChecks() {
  const { data: user } = useUser();
  const { data: termsAndConditions } = useTermsAndConditions();
  const { data: powerGenerationInfo } = usePowerGenerationInfo({
    queryConfig: {
      enabled: !!user,
    },
  });
  const { data: allocations } = usePersonalAllocations();

  // Count how many terms and conditions are false (not accepted)
  const unacceptedTermsCount = useMemo(() => {
    if (!termsAndConditions) return 0;
    return termsAndConditions.filter(term => !term.accepted).length;
  }, [termsAndConditions]);

  // Calculate unallocated panels for electricity
  const unallocatedElectricityPanels = useMemo(() => {
    if (!powerGenerationInfo?.user?.active || !allocations) return 0;
    
    const totalCapacity = powerGenerationInfo.user.active;
    const totalAllocated = allocations.reduce(
      (total, allocation) => total + (allocation.wattCapacity || 0),
      0
    );
    
    // Calculate unallocated capacity in watts, then convert to panels (100W each)
    const unallocatedWatts = Math.max(0, totalCapacity - totalAllocated);
    return Math.floor(unallocatedWatts / 100);
  }, [powerGenerationInfo?.user?.active, allocations]);

  // Check if user has any outstanding items
  const hasOutstandingTerms = unacceptedTermsCount > 0;
  const hasOutstandingAllocations = unallocatedElectricityPanels > 0;
  const hasAnyOutstanding = hasOutstandingTerms || hasOutstandingAllocations;

  return {
    unacceptedTermsCount,
    unallocatedElectricityPanels,
    hasOutstandingTerms,
    hasOutstandingAllocations,
    hasAnyOutstanding,
    termsAndConditions,
    allocations,
    powerGenerationInfo,
  };
}